import type { ReactNode } from 'react';

interface ProviderCardProps {
  /** Logo glyph rendered inside the 40px tile. */
  icon: ReactNode;
  name: ReactNode;
  /** Status badge rendered next to the name. */
  badge?: ReactNode;
  /** Sub-line beneath the name. */
  description?: ReactNode;
  /** Right-aligned action buttons. */
  actions?: ReactNode;
  /** Optional content rendered below the header row, separated by a divider. */
  children?: ReactNode;
  /** Render with a muted/placeholder treatment (used for not-yet-built providers). */
  muted?: boolean;
}

/** Compact provider card: a 40px logo tile, name + status badge, and right-aligned actions. */
export function ProviderCard({ icon, name, badge, description, actions, children, muted }: ProviderCardProps) {
  return (
    <div
      className={`rounded-md border p-3.5 ${
        muted ? 'border-dashed border-line bg-surface-2/40' : 'border-line bg-surface-2'
      }`}
    >
      <div className="flex items-center gap-3.5">
        <div
          className={`flex size-10 shrink-0 items-center justify-center rounded-tile bg-surface-4 ${
            muted ? 'opacity-70' : ''
          }`}
        >
          {icon}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${muted ? 'text-fg-secondary' : 'text-fg'}`}>{name}</span>
            {badge}
          </div>
          {description && <span className="truncate text-xs text-fg-tertiary">{description}</span>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
      </div>
      {children && <div className="mt-3.5 border-t border-line pt-3.5">{children}</div>}
    </div>
  );
}
